import React, {
	FC,
	useCallback,
	useContext,
	useEffect,
	useState,
	KeyboardEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import Select, { SingleValue } from 'react-select';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import { parsedCities } from '../../../helpers/cities';
import Textarea from '../../../components/bootstrap/forms/Textarea';
import Icon from '../../../components/icon/Icon';
import { apiRequest } from '../../../common/other/apiRequest';
import showNotification from '../../../components/extras/showNotification';
import { useTour } from '@reactour/tour';
import { RegisterValidations } from './RegisterValidations';
import Checks from '../../../components/bootstrap/forms/Checks';
import AgreementModal from './AgreeMentModal';
import KvkkModal from './KvkkModal';

import { capitalizeFirstLetter } from '../../../helpers/helpers';

interface ILoginHeaderProps {
	isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className='text-center h1 fw-bold mt-5'>Hesabını Oluştur,</div>
				<div className='text-center h4 text-muted mb-5'>
					Lojistigonun eşsiz ayrıcalıkları için kayıt ol !
				</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Hoşgeldiniz,</div>
			<div className='text-center h4 text-muted mb-5'>
				Devam etmek için lütfen giriş yapınız!
			</div>
		</>
	);
};

interface ILoginProps {
	isSignUp?: boolean;
}
const apiUrl = process.env.REACT_APP_API_BASE_URL;

const Login: FC<ILoginProps> = ({ isSignUp = false }) => {
	const { setIsOpen } = useTour();
	const { setUser } = useContext(AuthContext);
	const { setUserData } = useContext(AuthContext);
	const { setUserType } = useContext(AuthContext);
	const [isAgreed, setIsAgreed] = useState(false);
	const [isKvkkAgreed, setIsKvkkAgreed] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);
	const [disableStaus, setDisableStatus] = useState(false);
	const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
	const { darkModeStatus } = useDarkMode();
	const [margin, setMargin] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [userName, setUserName] = useState<string | undefined>(undefined);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
	useEffect(() => {
		setSingUpStatus(!!isSignUp);
	}, [isSignUp]);
	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/'), [navigate]);
	const [isLoadingSignUp, setIsLoadingSignUp] = useState<boolean>(false);

	const loginFormik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: Record<string, string> = {};
			if (!values.loginUsername) errors.loginUsername = 'Lütfen mail adresinizi giriniz.';
			if (!values.loginPassword) errors.loginPassword = 'Şifrenizi giriniz!';
			return errors;
		},
		validateOnChange: false,
		onSubmit: async ({ loginUsername, loginPassword }) => {
			try {
				setDisableStatus(true);
				const response = await apiRequest({
					url: '/token',
					method: 'POST',
					body: { email: loginUsername, password: loginPassword },
				});

				const { token, userData, message } = response?.data || {};

				if (response?.status === 200 && token && userData) {
					try {
						localStorage.setItem('token', token);
						localStorage.setItem('userId', String(userData.id || 0));
						localStorage.setItem('ljs_asideStatus', 'true');

						setUser?.(userData.username || userData.firstname || userData.email || 'user');
						setUserData?.(userData);
						setUserType?.(userData.type);

						showNotification(message || 'Başarılı giriş.', 'Başarılı', 'success', 2000);

						setTimeout(() => {
							setDisableStatus(false);
							handleOnClick?.();
						}, 1000);
					} catch (e) {
						console.error("Login success processing error:", e);
						showNotification('Hata', 'Giriş işlemi sırasında bir sorun oluştu.', 'danger');
						setDisableStatus(false);
					}
				} else {
					setDisableStatus(false);
					showNotification('Hata', response?.data?.message || 'Bilinmeyen hata', 'danger');
				}
			} catch (error: any) {
				showNotification('Hata', error?.response?.data?.message || 'Beklenmeyen bir hata oluştu.', 'warning');
				setDisableStatus(false);
			}
		},
	});

	const registerFormik = useFormik({
		enableReinitialize: true,
		initialValues: {
			email: '',
			password: '',
			confirmpassword: '',
			firstname: '',
			lastname: '',
			phone_code: '+90',
			phone_number: '',
			reference_code: '',
		},
		validationSchema: RegisterValidations,
		onSubmit: async (values) => {
			console.log(values);
			if (!isAgreed || !isKvkkAgreed) {
				showNotification('Hata', 'Lütfen Sözleşmeleri Kabul Ediniz', 'danger');
				return;
			}

			try {
				setIsLoadingSignUp(true);
				const response = await apiRequest({
					method: 'POST',
					url: '/register',
					body: values,
				});

				if (response?.status === 200) {
					showNotification('Kayıt işlemi başarılı', 'Başarılı!', 'success');
					setTimeout(() => window.location.reload(), 2000);
				} else {
					const { errors, message } = response.data;
					const errorMsg = errors
						? Object.values(errors).flat().join(', ')
						: message || 'Bir hata oluştu';
					showNotification('Hata!', errorMsg, 'danger');
				}
			} catch (error: any) {
				showNotification('Hata!', error?.response?.data?.message || 'Hata oluştu', 'danger');
			} finally {
				setIsLoadingSignUp(false);
			}
		},
	});

	const handleContinue = async () => {
		try {
			setIsLoading(true);
			setSignInPassword(false);

			const response = await apiRequest({
				url: '/checkUsers',
				method: 'POST',
				body: { email: loginFormik.values.loginUsername },
				multipart: true,
			});

			const { message, userName: foundUserName } = response.data || {};
			const status = response?.status;

			showNotification(message, status === 200 ? 'Başarılı' : 'Başarısız', status === 200 ? 'success' : 'danger');

			setTimeout(() => {
				if (status === 200) {
					setUserName(foundUserName);
					setSignInPassword(true);
					document.getElementById('loginPassword')?.focus();
				} else {
					loginFormik.setFieldError('loginUsername', message || 'Kullanıcı bulunamadı');
				}
				setIsLoading(false);
			}, 1000);
		} catch (error) {
			console.error('Hata detayları:', error); // <- Hata burada görülecek
			loginFormik.setFieldError('loginUsername', 'Sistemde kayıtlı kullanıcı bulunamadı');
			setSignInPassword(false);
			setIsLoading(false);
		}
	};

	const handleEnterKey = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			handleContinue();
		}
	};

	const handleEnterKeyPassword = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			loginFormik.submitForm();
		}
	};

	const appVersion = process.env.REACT_APP_VERSION;

	return (
		<PageWrapper
			isProtected={false}
			title={singUpStatus ? 'Kayıt Ol' : 'Giriş Yap'}
			// className={classNames({
			// 	'bg-dark': !singUpStatus,
			// 	'bg-light': singUpStatus,
			// })}
			className='bg-light'>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xxl-6 col-lg-8 col-md-10 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-1'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}
										aria-label='Facit'>
										<Logo height={200} />
									</Link>
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}>
									<div className='row row-cols-2 g-3 pb-3 px-3 mt-0'>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={singUpStatus}
												className='rounded-1 w-100'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												Giriş Yap
											</Button>
										</div>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={!singUpStatus}
												className='rounded-1 w-100'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												Kayıt Ol
											</Button>
										</div>
									</div>
								</div>

								<LoginHeader isNewUser={singUpStatus} />

								<form className='row g-4' onSubmit={singUpStatus ? registerFormik.handleSubmit : loginFormik.handleSubmit}>
									{singUpStatus ? (
										<>
											<div className='col-md-6'>
												<FormGroup id='firstname' isFloating label='Adınız'>
													<Input
														autoComplete='given-name'
														value={registerFormik.values.firstname}
														isTouched={registerFormik.touched.firstname}
														invalidFeedback={
															registerFormik.errors.firstname
														}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																firstname: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-6'>
												<FormGroup
													id='lastname'
													isFloating
													label='Soyadınız'>
													<Input
														autoComplete='family-name'
														value={registerFormik.values.lastname}
														isTouched={registerFormik.touched.lastname}
														invalidFeedback={
															registerFormik.errors.lastname
														}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																lastname: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-6'>
												<FormGroup id='email' isFloating label='Email'>
													<Input
														autoComplete='email'
														value={registerFormik.values.email}
														isTouched={registerFormik.touched.email}
														invalidFeedback={registerFormik.errors.email}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																...registerFormik.errors,
																email: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-6'>
												<FormGroup
													id='phone_number'
													isFloating
													label='Telefon No'>
													<Input
														maxLength={10}
														autoComplete='tel'
														type='number'
														value={registerFormik.values.phone_number}
														isTouched={
															registerFormik.touched.phone_number
														}
														invalidFeedback={
															registerFormik.errors.phone_number
														}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																phone_number: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-6'>
												<div className='d-flex justify-content-between align-items-center position-relative'>
													<FormGroup
														id='password'
														isFloating
														label='Şifre'
														className='col'
													>
														<Input
															type={passwordVisible ? 'text' : 'password'}
															autoComplete='new-password'
															className='pe-5' // sağ padding ekledik
															value={registerFormik.values.password}
															isTouched={registerFormik.touched.password}
															validFeedback='İyi Görünüyor.'
															invalidFeedback={registerFormik.errors.password}
															isValid={registerFormik.isValid}
															onChange={registerFormik.handleChange}
															onBlur={registerFormik.handleBlur}
															onFocus={() => {
																setMargin(15);
																registerFormik.setErrors({
																	...registerFormik.errors,
																	password: undefined,
																});
															}}
														/>
													</FormGroup>
												</div>
											</div>
											<div className='col-md-6'>
												<FormGroup
													id='confirmpassword'
													isFloating
													label='Şifre(Tekrar)'>
													<Input
														type='password'
														value={registerFormik.values.confirmpassword}
														isTouched={
															registerFormik.touched.confirmpassword
														}
														invalidFeedback={
															registerFormik.errors.confirmpassword
														}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																...registerFormik.errors,
																confirmpassword: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-6'>
												<FormGroup
													id='reference_code'
													isFloating
													label='Referans Kodu'>
													<Input
														value={registerFormik.values.reference_code}
														isTouched={
															registerFormik.touched.reference_code
														}
														invalidFeedback={
															registerFormik.errors.reference_code
														}
														isValid={registerFormik.isValid}
														onChange={registerFormik.handleChange}
														onBlur={registerFormik.handleBlur}
														onFocus={() => {
															registerFormik.setErrors({
																reference_code: undefined,
															});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-md-12 '>
												<div className='d-flex '>
													<Checks
														checked={isAgreed}
														type='checkbox'
														onClick={() => setIsAgreed(!isAgreed)}
													/>
													<p>
														<a
															href='#'
															onClick={(e) => {
																e.preventDefault();
																setIsModalOpen(true);
															}}
															rel='noopener noreferrer'>
															Kullanıcı Sözleşmesi
														</a>
														'ni okudum ve onaylıyorum
													</p>
												</div>
												<div className='d-flex'>
													<Checks
														checked={isKvkkAgreed}
														type='checkbox'
														onClick={() =>
															setIsKvkkAgreed(!isKvkkAgreed)
														}
													/>
													<p>
														<a
															href='#'
															onClick={(e) => {
																e.preventDefault();
																setIsKvkkModalOpen(true);
															}}
															rel='noopener noreferrer'>
															Kvkk Onay Metni
														</a>
														'ni okudum ve onaylıyorum
													</p>
												</div>
											</div>
											<div className='col-12'>
												<div className='alert alert-warning' role='alert'>
													<Icon
														icon={'info'}
														color='dark'
														className='btn-icon'
													/>
													<span style={{ marginLeft: 10 }}>
														Whatsapp Destek Hattımız :
													</span>
													<a
														href='https://wa.me/905367333537'
														target='_blank'
														style={{
															marginLeft: 5,
															textDecoration: 'none',
															color: 'blue',
														}}>
														0536 733 35 37
														<Icon
															icon={'Phone'}
															color='info'
															className='btn-icon'
															style={{
																marginLeft: 5,
															}}
														/>
													</a>
												</div>
											</div>
											<div className='col-12'>
												<Button
													color='info'
													className='w-100 py-3'
													type='submit'
													isDisable={isLoadingSignUp}>
													{isLoadingSignUp && (
														<Spinner isSmall inButton isGrow />
													)}
													Kayıt Ol
												</Button>
											</div>
										</>
									) : (
										<>
											<div className='col-12'>
												<FormGroup
													id='loginUsername'
													isFloating
													label='Email'
													className={classNames({
														'd-none': signInPassword,
													})}>
													<Input
														autoComplete='username'
														value={loginFormik.values.loginUsername}
														isTouched={loginFormik.touched.loginUsername}
														invalidFeedback={
															loginFormik.errors.loginUsername
														}
														isValid={loginFormik.isValid}
														onChange={loginFormik.handleChange}
														onBlur={loginFormik.handleBlur}
														onKeyDown={handleEnterKey}
														onFocus={() => {
															loginFormik.setErrors({});
														}}
													/>
												</FormGroup>
												{signInPassword && (
													<div className='text-center h4 mb-3 fw-bold'>
														Merhaba, {capitalizeFirstLetter(userName)}
													</div>
												)}
												<FormGroup
													id='loginPasswordGroup'
													isFloating
													label='Şifre'
													className={classNames({
														'd-none': !signInPassword,
													})}>
													<Input
														id='loginPassword'
														name='loginPassword'
														component='InputMask'
														type='password'
														autoComplete='current-password'
														value={loginFormik.values.loginPassword}
														isTouched={loginFormik.touched.loginPassword}
														invalidFeedback={
															loginFormik.errors.loginPassword
														}
														validFeedback='İyi Görünüyor.'
														isValid={loginFormik.isValid}
														onChange={loginFormik.handleChange}
														onBlur={loginFormik.handleBlur}
														onKeyDown={handleEnterKeyPassword}
													/>
												</FormGroup>
											</div>
											<div className='col-12 text-center'>
												<a
													href='/forgot-password'
													rel='noopener noreferrer'
													target='_blank'
													className={classNames(
														'text-decoration-none me-3 text-info fw-bold',
														{
															'link-light': singUpStatus,
															'link-dark': !singUpStatus,
														},
													)}>
													Şifremi unuttum
												</a>
											</div>
											<div className='col-12'>
												<div className='alert alert-warning' role='alert'>
													<Icon
														icon={'info'}
														color='dark'
														className='btn-icon'
													/>
													<span style={{ marginLeft: 10 }}>
														Whatsapp Destek Hattımız :
													</span>
													<a
														href='https://wa.me/905367333537'
														target='_blank'
														style={{
															marginLeft: 5,
															textDecoration: 'none',
															color: 'blue',
														}}>
														0536 733 35 37
														<Icon
															icon={'Phone'}
															color='info'
															className='btn-icon'
															style={{
																marginLeft: 5,
															}}
														/>
													</a>
												</div>
											</div>
											<div className='col-12'>
												{!signInPassword ? (
													<Button
														color='info'
														className='w-100 py-3'
														isDisable={
															!loginFormik.values.loginUsername ||
															isLoading
														}
														onClick={handleContinue}>
														{isLoading && (
															<Spinner isSmall inButton isGrow />
														)}
														Devam
													</Button>
												) : (
													<Button
														isDisable={disableStaus}
														color='warning'
														className='w-100 py-3'
														onClick={loginFormik.handleSubmit}
								type='submit'>
														{disableStaus && (
															<Spinner isSmall inButton isGrow />
														)}
														Giriş Yap
													</Button>
												)}
											</div>
										</>
									)}
								</form>
							</CardBody>
						</Card>
						<div className='text-center'>
							<a
								href='/'
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Gizlilik Sözleşmesi
							</a>
							<a
								href='/'
								className={classNames('link-light text-decoration-none', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Kullanım Koşulları
							</a>
							<p className='ms-2'>Versiyon : {appVersion}</p>
						</div>

					</div>
				</div>
				<AgreementModal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} />
				<KvkkModal
					isOpen={isKvkkModalOpen}
					toggle={() => setIsKvkkModalOpen(!isKvkkModalOpen)}
				/>
			</Page>
		</PageWrapper>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};

export default Login;
